/**
 * @fileoverview AR-924 — State of the teacher deliverable drawer: loads through the teacher-upload port
 * and exposes the writes. A successful write replaces `data` with the refreshed payload and notifies `onChanged`.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getTeacherDeliverablePort, TeacherDeliverableError } from '../../teacherUpload'
import type { UploadProgress } from '../../teacherUpload'
import type { AddLinkOutcome } from '../linkUtils'
import type { TaskTrackDeliverable } from '../../types'
import { errorMessageFor, notifyError, notifySuccess } from './feedback'
import { useRemovingIds } from './useRemovingIds'

const PERCENT = 100

export interface TeacherDeliverableState {
  loading: boolean
  errored: boolean
  data: TaskTrackDeliverable | null
  uploadPercent: number | null
  removingIds: ReadonlySet<string>
  savingDate: boolean
  busy: boolean
  reload: () => void
  upload: (files: File[], workCompletedAt?: string) => Promise<boolean>
  cancelUpload: () => void
  removeFile: (fileId: string) => Promise<boolean>
  addLink: (url: string) => Promise<AddLinkOutcome>
  removeLink: (linkId: string) => Promise<boolean>
  saveWorkCompletedAt: (workCompletedAt: string) => Promise<boolean>
}

interface UseTeacherDeliverableArgs {
  activityId: string
  studentId: string
  opened: boolean
  onChanged?: () => void
}

function percentOf({ loaded, total }: UploadProgress): number {
  if (!total) return 0
  return Math.round((loaded / total) * PERCENT)
}

function isCancellation(error: unknown): boolean {
  return error instanceof TeacherDeliverableError && error.kind === 'cancelled'
}

function isInvalidUrl(error: unknown): boolean {
  return error instanceof TeacherDeliverableError && error.kind === 'invalid_url'
}

export function useTeacherDeliverable(args: UseTeacherDeliverableArgs): TeacherDeliverableState {
  const { activityId, studentId, opened, onChanged } = args
  const { t } = useTranslation()
  const port = getTeacherDeliverablePort()
  const [loading, setLoading] = useState(true)
  const [errored, setErrored] = useState(false)
  const [data, setData] = useState<TaskTrackDeliverable | null>(null)
  const [uploadPercent, setUploadPercent] = useState<number | null>(null)
  const { removingIds, withRemoving } = useRemovingIds()
  const [savingDate, setSavingDate] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)
  const uploadAbort = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!opened || !activityId || !studentId) return undefined
    let cancelled = false
    setLoading(true)
    setErrored(false)
    port.fetch(activityId, studentId)
      .then(result => { if (!cancelled) setData(result) })
      .catch(() => { if (!cancelled) setErrored(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [opened, activityId, studentId, reloadToken, port])

  const run = useCallback(async (
    request: () => Promise<TaskTrackDeliverable>,
    successKey: string | null,
    errorKey: string,
  ): Promise<boolean> => {
    try {
      setData(await request())
      onChanged?.()
      if (successKey) notifySuccess(t(`task_tracker.teacher_upload.${successKey}`))
      return true
    } catch (error) {
      if (isCancellation(error)) return false
      notifyError(errorMessageFor(t, error, errorKey))
      return false
    }
  }, [t, onChanged])

  const upload = useCallback(async (files: File[], workCompletedAt?: string) => {
    const controller = new AbortController()
    uploadAbort.current = controller
    setUploadPercent(0)
    const sendFiles = async () => {
      const uploaded = await port.uploadFiles({
        activityId,
        studentId,
        files,
        signal: controller.signal,
        onProgress: progress => setUploadPercent(percentOf(progress)),
      })
      if (!workCompletedAt) return uploaded
      // The backend ignores the date on upload: it is only stored by the PATCH, once the delivery exists.
      // A failed PATCH keeps the uploaded files and only reports the date.
      try {
        return await port.updateWorkCompletedAt(activityId, studentId, workCompletedAt)
      } catch (error) {
        notifyError(errorMessageFor(t, error, 'date_error'))
        return uploaded
      }
    }
    try {
      return await run(sendFiles, 'upload_success', 'upload_error')
    } finally {
      uploadAbort.current = null
      setUploadPercent(null)
    }
  }, [run, port, activityId, studentId, t])

  const cancelUpload = useCallback(() => uploadAbort.current?.abort(), [])

  const removeFile = useCallback((fileId: string) => {
    const request = () => port.removeFile(activityId, studentId, fileId)
    return withRemoving(fileId, () => run(request, null, 'remove_error'))
  }, [withRemoving, run, port, activityId, studentId])

  const removeLink = useCallback((linkId: string) => {
    const request = () => port.removeLink(activityId, studentId, linkId)
    return withRemoving(linkId, () => run(request, null, 'remove_error'))
  }, [withRemoving, run, port, activityId, studentId])

  // 422 "invalid url" → inline error in the modal; other failures toast here.
  const addLink = useCallback(async (url: string): Promise<AddLinkOutcome> => {
    try {
      setData(await port.addLink(activityId, studentId, url))
      onChanged?.()
      notifySuccess(t('task_tracker.teacher_upload.link_added'))
      return 'success'
    } catch (error) {
      if (isInvalidUrl(error)) return 'invalid'
      notifyError(errorMessageFor(t, error, 'remove_error'))
      return 'error'
    }
  }, [port, activityId, studentId, t, onChanged])

  const saveWorkCompletedAt = useCallback(async (workCompletedAt: string) => {
    const request = () => port.updateWorkCompletedAt(activityId, studentId, workCompletedAt)
    setSavingDate(true)
    try {
      return await run(request, 'date_updated', 'date_error')
    } finally {
      setSavingDate(false)
    }
  }, [run, port, activityId, studentId])

  const reload = useCallback(() => setReloadToken(token => token + 1), [])
  const busy = uploadPercent !== null || savingDate || removingIds.size > 0

  return {
    loading,
    errored,
    data,
    uploadPercent,
    removingIds,
    savingDate,
    busy,
    reload,
    upload,
    cancelUpload,
    removeFile,
    addLink,
    removeLink,
    saveWorkCompletedAt,
  }
}
